// A program with lessons, opened from the Programs board with Continue. It
// opens over the board in the Build a program frame, instead of the /designer
// page (Cian, 2026-10-16; the frame and tabs are from Ronan's capability
// prototype). Three tabs:
//   1. Training: today's Program Designer, unchanged.
//   2. Set up: the choices made when it was built. Each saves the way the
//      program card's own editors do, so the card and this tab always agree.
//   3. People: everyone in the program, Invite People, and Remove on a person's row.
// A Guided Course or Hybrid also matches mentors: Open Matches takes the admin
// to its Matches page, as the /designer page's Designer | Matches switch did.
// The board's URL names no program (`admin` sits where its id would be), so
// everything inside reads the program from ProgramGroupIdContext.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ConfirmDialog from '../../../../../../../components/DialogUi/ConfirmDialog';
import DosenButton from '../../../../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import { ProgramGroupIdContext, useProgramContext } from '../../../../../../../hooks/useProgramContext';
import useGroupDetails from '../../../../../../../queries/useGroupDetails';
import { useAppSelector } from '../../../../../../../redux/hooks';
import { SettingsLayout } from '../../program tabs/ProgramSettings/SettingsLayout';
import { TitleTabs } from './editorUi';
import ProgramSetupTab, { ProgramSetupTabProps } from './ProgramSetupTab';
import ProgramPeopleTab from './ProgramPeopleTab';
import type { ProgramCardData } from '../types';

type Tab = 'training' | 'setup' | 'people';

const TABS: { key: Tab; label: string }[] = [
  { key: 'training', label: 'Training' },
  { key: 'setup', label: 'Set up' },
  { key: 'people', label: 'People' }
];

type Props = Omit<ProgramSetupTabProps, 'program'> & {
  /** The program to open; null when the modal is closed. */
  program: ProgramCardData | null;
  onClose: () => void;
  /** After People removes someone, so the board reads its programs again (the card's completion). */
  onPeopleChanged?: () => void;
};

const ProgramEditorModal: React.FC<Props> = ({ program, ...rest }) =>
  program ? (
    <ProgramGroupIdContext.Provider value={program.id}>
      <EditorDialog program={program} {...rest} />
    </ProgramGroupIdContext.Provider>
  ) : null;

const EditorDialog: React.FC<Props & { program: ProgramCardData }> = ({
  program,
  onClose,
  onPeopleChanged,
  ...setup
}) => {
  const [tab, setTab] = useState<Tab>('training');
  const [fullScreen, setFullScreen] = useState(false);
  const navigate = useNavigate();
  const { location, orgId, coId } = useProgramContext();
  // Edits in Training that leaving would lose, and where the admin was going when the "close anyway?"
  // question came up (closing, or the program's Matches page).
  const [pendingEdits, setPendingEdits] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState<(() => void) | null>(null);
  const leave = (go: () => void) => (pendingEdits ? setConfirmLeave(() => go) : go());
  const requestClose = () => leave(onClose);
  // Guided Course and Hybrid match mentors too (ProgramDetails' rule: every model but a Course). Mentorship
  // never opens here, and a program with no model yet has no Matches to open.
  const hasMatches = program.model !== null && program.model !== 'masterclass';
  const openMatches = () => leave(() => navigate(`/app/programdetails/${orgId}/${coId}/${program.id}/matches`));

  // On the /designer page ProgramDetails loads the group; over the board, the modal does.
  const { refetch } = useGroupDetails(program.id, location);
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id]);

  // The group's own name once it has loaded (a rename in Training shows at once), else the card's.
  const group = useAppSelector((s) => s.groupDetails.data);
  const title = (String(group?.grpId ?? '') === program.id && group?.name) || program.name;

  return (
    <Dialog
      open
      // Only the X closes it: a click outside or Esc would drop unsaved lesson edits.
      onClose={(_e, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') requestClose();
      }}
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }
        }
      }}
      PaperProps={{
        sx: {
          width: fullScreen ? '100vw' : '96vw',
          maxWidth: fullScreen ? '100vw' : '96vw',
          height: fullScreen ? '100vh' : '94vh',
          maxHeight: fullScreen ? '100vh' : undefined,
          m: fullScreen ? 0 : 2,
          bgcolor: '#0F0F0F',
          border: fullScreen ? 'none' : '1px solid #444444',
          borderRadius: fullScreen ? 0 : '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.2s ease, height 0.2s ease, max-width 0.2s ease'
        }
      }}
    >
      <Box
        sx={{
          px: '24px',
          pt: '18px',
          borderBottom: '1px solid #2A2A2A',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography
            noWrap
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              color: '#FDFDFD',
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              fontWeight: 700
            }}
          >
            {title}
          </Typography>
          {hasMatches && (
            <DosenButton tone="basic" sizeVariant="sm" sx={{ borderColor: '#565656' }} onClick={openMatches}>
              Open Matches
            </DosenButton>
          )}
          <DosenTooltip title={fullScreen ? 'Exit full screen' : 'Full screen'}>
            <IconButton
              onClick={() => setFullScreen((v) => !v)}
              aria-label={fullScreen ? 'Exit full screen' : 'Full screen'}
              sx={{ width: 26, height: 26, p: 0, color: '#FDFDFD' }}
            >
              {fullScreen ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </DosenTooltip>
          <IconButton onClick={requestClose} aria-label="Close program" sx={{ width: 22, height: 22, p: 0 }}>
            <CloseIcon sx={{ color: '#FDFDFD', fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ pb: '10px' }}>
          <TitleTabs tabs={TABS} active={tab} onTab={(k) => setTab(k as Tab)} />
        </Box>
      </Box>

      {/* Training stays mounted behind the other tabs, so a visit to Set up or People keeps unsaved edits. */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, overflowY: 'auto', display: tab === 'training' ? 'block' : 'none' }}>
          <SettingsLayout view="design" refetchKey={0} onPendingChange={setPendingEdits} />
        </Box>
        {tab !== 'training' && (
          <Box sx={{ position: 'absolute', inset: 0, overflowY: 'auto', px: '32px', py: '24px' }}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
              {tab === 'setup' ? (
                <ProgramSetupTab program={program} {...setup} />
              ) : (
                <ProgramPeopleTab program={program} orgMembers={setup.orgMembers} onPeopleChanged={onPeopleChanged} />
              )}
            </Box>
          </Box>
        )}
      </Box>
      <ConfirmDialog
        open={confirmLeave !== null}
        title="Close without saving?"
        message="You have changes in Training that aren't saved. If you close now, they'll be lost."
        confirmLabel="Close Without Saving"
        cancelLabel="Keep Editing"
        onConfirm={() => {
          const go = confirmLeave;
          setConfirmLeave(null);
          go?.();
        }}
        onClose={() => setConfirmLeave(null)}
      />
    </Dialog>
  );
};

export default ProgramEditorModal;
